import { LivePhoto, Picture as PictureType } from '@/@types/common'
import LightBox from '@/components/LightBox/LightBox'
import { Box } from '@mantine/core'
import { MouseEvent, useState } from 'react'
import type { SlideImage as BaseSlideImage } from 'yet-another-react-lightbox'
import Picture from '../Picture'
import ImageGrid, { ImageGridProps } from './ImageGrid'
import styles from './ImageGridSection.module.css'

interface SlideImage extends BaseSlideImage {
	livePhoto?: LivePhoto
}

export interface ImageGridSectionProps<DataItem extends PictureType = PictureType> extends Omit<
	ImageGridProps<DataItem>,
	'data' | 'onItemClick'
> {
	data: DataItem[]
	onItemClick?: ImageGridProps<DataItem>['onItemClick']
}

export default function ImageGridSection<DataItem extends PictureType = PictureType>({
	data,
	onItemClick,
	...props
}: ImageGridSectionProps<DataItem>) {
	const [open, setOpen] = useState(false)
	const [index, setIndex] = useState(0)

	const slides = data.map((item) => ({
		src: item.picUrl,
		width: item.width,
		height: item.height,
		livePhoto: item.livePhoto,
	}))

	const handleItemClick = (
		item: DataItem,
		itemIndex: number,
		e: MouseEvent<Element, globalThis.MouseEvent>
	) => {
		setIndex(itemIndex)
		setOpen(true)
	}

	if (data.length === 0) {
		return null
	}

	return (
		<Box>
			<ImageGrid data={data} onItemClick={onItemClick ?? handleItemClick} {...props} />
			<div onClick={(e) => e.stopPropagation()}>
				<LightBox
					open={open}
					close={() => setOpen(false)}
					index={index}
					slides={slides}
					className={styles.lightbox}
					render={{
						slide: ({ slide, offset }) => {
							const item = slide as SlideImage
							const isCurrent = offset === 0
							if (item.livePhoto) {
								return (
									<Picture
										autoPlayOnce={isCurrent}
										isLivePhoto
										data={item.livePhoto}
										src={item.src}
										width={item.width}
										height={item.height}
									/>
								)
							}
							return undefined
						},
					}}
				/>
			</div>
		</Box>
	)
}
